import React, { useEffect, useRef, useState } from "react";
import { apiUrl } from "../apiConfig";
import "./Careers.css";

/**
 * Careers section — job listings grouped/filterable by category.
 * Each job card can expand its description ("Show more") and opens
 * an "Apply for Position" modal with the application form.
 */

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
}

function JobCard({ job, index, onApply }) {
  const [expanded, setExpanded] = useState(false);

  // Ensure requirements is always an array
  const requirements = Array.isArray(job.requirements) ? job.requirements : [];

  return (
    <article className="job-card" style={{ "--i": index }}>
      <div className="job-card__top">
        <h3 className="job-card__title">{job.title || "Untitled Position"}</h3>
        <span className="job-card__category">{job.category || "General"}</span>
      </div>

      <p className="job-card__desc">{expanded ? job.fullDesc : job.shortDesc}</p>
      <button
        type="button"
        className="job-card__more"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? "Show less" : "Show more"}
      </button>

      <div className="job-card__meta">
        <span className="job-card__meta-item">
          <strong>Location:</strong> {job.location || "Not specified"}
        </span>
        <span className="job-card__meta-item">
          <strong>Type:</strong> {job.type || "Not specified"}
        </span>
      </div>

      {requirements.length > 0 && (
        <>
          <p className="job-card__requirements-title">Requirements</p>
          <ul className="job-card__requirements">
            {requirements.map((req, index) => (
              <li key={index}>{req}</li>
            ))}
          </ul>
        </>
      )}

      <button type="button" className="job-card__apply btn btn-accent" onClick={() => onApply(job)}>
        Apply Now
      </button>
    </article>
  );
}

function ApplyModal({ job, onClose }) {
  const [fileName, setFileName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setFileName(file ? file.name : "");
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  
  // ✅ إنشاء FormData من النموذج
  const form = e.target;
  const formData = new FormData(form);
  
  // ✅ إضافة jobId و jobTitle
  formData.append('jobId', job.id);
  formData.append('jobTitle', job.title);
  
  // ✅ للتصحيح: عرض محتويات FormData
  console.log('Job ID:', job.id);
  console.log('Job Title:', job.title);
  for (let pair of formData.entries()) {
    console.log(pair[0] + ': ' + pair[1]);
  }
  
  setIsSubmitting(true);
  setError('');
  
  try {
    const response = await fetch(apiUrl('/applications.php'), {
      method: 'POST',
      body: formData,
      // ✅ لا تحدد Content-Type - المتصفح سيحددها تلقائياً مع FormData
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Unable to submit application.');
    }
    
    setSubmitted(true);
  } catch (err) {
    console.error('Submission error:', err);
    setError(err.message || 'Something went wrong. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <div className="apply-modal" role="dialog" aria-modal="true" aria-label={`Apply for ${job.title}`}>
      <div className="apply-modal__backdrop" onClick={onClose} />
      <div className="apply-modal__panel">
        <div className="apply-modal__header">
          <div>
            <p className="apply-modal__title">Apply for Position</p>
            <p className="apply-modal__subtitle">{job.title}</p>
          </div>
          <button type="button" className="apply-modal__close" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {submitted ? (
          <div className="apply-form__success">
            <span className="apply-form__success-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            <p className="apply-form__success-title">Application Submitted</p>
            <p className="apply-form__success-text">
              Thanks for applying to {job.title}. Our HR team will be in touch if
              there's a match.
            </p>
            <button 
              type="button" 
              className="btn btn-accent apply-form__close"
              onClick={onClose}
              style={{ marginTop: '20px' }}
            >
              Close
            </button>
          </div>
        ) : (
          <form className="apply-form" onSubmit={handleSubmit} encType="multipart/form-data">
            {error && (
              <div className="apply-form__error" style={{ 
                color: '#e6493f', 
                background: 'rgba(230,73,63,0.08)', 
                padding: '12px 16px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <div className="apply-form__field">
              <label htmlFor="apply-name">
                Full Name <span>*</span>
              </label>
              <input 
                id="apply-name" 
                name="fullName" 
                type="text" 
                required 
                placeholder="Your full name" 
              />
            </div>

            <div className="apply-form__row">
              <div className="apply-form__field">
                <label htmlFor="apply-email">
                  Email Address <span>*</span>
                </label>
                <input 
                  id="apply-email" 
                  name="email" 
                  type="email" 
                  required 
                  placeholder="you@example.com" 
                />
              </div>

              <div className="apply-form__field">
                <label htmlFor="apply-phone">
                  Phone Number <span>*</span>
                </label>
                <input 
                  id="apply-phone" 
                  name="phone" 
                  type="tel" 
                  required 
                  placeholder="+962 ..." 
                />
              </div>
            </div>

            <div className="apply-form__field">
              <label htmlFor="apply-experience">
                Years of Experience <span>*</span>
              </label>
              <input
                id="apply-experience"
                name="experience"
                type="number"
                min="0"
                max="50"
                required
                placeholder="e.g. 3"
              />
              <span className="apply-form__hint">Must be between 0 and 50 years</span>
            </div>

            <div className="apply-form__field">
              <label htmlFor="apply-resume">
                Resume/CV <span>*</span>
              </label>
              <label
                className={`apply-form__upload ${fileName ? "apply-form__upload--filled" : ""}`}
                htmlFor="apply-resume"
              >
                <span className="apply-form__upload-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v12" />
                    <path d="m7 8 5-5 5 5" />
                    <path d="M4 17v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
                  </svg>
                </span>
                <span className="apply-form__upload-text">
                  <span className="apply-form__upload-label">
                    {fileName || "Upload your resume (PDF or Word)"}
                  </span>
                  <span className="apply-form__upload-hint">Max file size: 5MB</span>
                </span>
                <input
                  id="apply-resume"
                  name="resume"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  required
                  onChange={handleFileChange}
                />
              </label>
            </div>

            <div className="apply-form__field">
              <label htmlFor="apply-cover">Cover Letter (Optional)</label>
              <textarea 
                id="apply-cover" 
                name="coverLetter" 
                placeholder="Tell us why you're a good fit"
                rows="4"
              />
            </div>

            <div className="apply-form__actions">
              <button type="button" className="apply-form__cancel" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-accent apply-form__submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Careers() {
  const [sectionRef, isVisible] = useReveal();
  const [activeCategory, setActiveCategory] = useState("All");
  const [applyingJob, setApplyingJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Load jobs from API
  useEffect(() => {
    const loadJobs = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await fetch(apiUrl("/jobs.php"));
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Unable to load jobs.");
        }

        // Handle the API response format: { success: true, jobs: [...] }
        if (data && data.success === true && Array.isArray(data.jobs)) {
          // Parse requirements if they're stored as JSON strings
          const parsedJobs = data.jobs.map(job => ({
            ...job,
            requirements: typeof job.requirements === 'string' 
              ? JSON.parse(job.requirements) 
              : (Array.isArray(job.requirements) ? job.requirements : [])
          }));
          setJobs(parsedJobs);
        } else if (Array.isArray(data)) {
          setJobs(data);
        } else {
          console.warn("Unexpected jobs data format:", data);
          setJobs([]);
        }
      } catch (err) {
        setError(err.message || "Unable to load jobs. Please try again later.");
        setJobs([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadJobs();
  }, []);

  // Get unique categories from jobs
  const categories = ["All", ...Array.from(new Set(jobs.map((job) => job.category).filter(Boolean)))];
  
  // Filter jobs by category
  const filteredJobs = activeCategory === "All" 
    ? jobs 
    : jobs.filter((job) => job.category === activeCategory);

  return (
    <section
      className={`careers ${isVisible ? "careers--visible" : ""}`}
      id="careers"
      ref={sectionRef}
    >
      <span className="careers__accent" aria-hidden="true" />

      <div className="container careers__container">
        <div className="careers__header">
          <p className="careers__eyebrow">Join Our Team</p>
          <h2 className="careers__title">
            Open <span>Positions</span>
          </h2>
          <p className="careers__intro">
            We're always looking for skilled, driven people to help us build
            Jordan's next generation of engineering landmarks. Browse our
            current openings below.
          </p>
        </div>

        {/* Category filters */}
        <div className="careers__filters">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className={`careers__filter ${
                category === activeCategory ? "careers__filter--active" : ""
              }`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="careers__empty">
            Loading job postings...
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <p className="careers__empty">{error}</p>
        )}

        {/* Job list */}
        {!isLoading && !error && (
          <div className="careers__list">
            {filteredJobs.length === 0 ? (
              <p className="careers__empty">
                {jobs.length === 0 
                  ? "No job openings available at the moment. Please check back later." 
                  : "No open positions in this category right now."}
              </p>
            ) : (
              filteredJobs.map((job, index) => (
                <JobCard 
                  key={job.id || index} 
                  job={job} 
                  index={index} 
                  onApply={setApplyingJob} 
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Apply modal */}
      {applyingJob && (
        <ApplyModal job={applyingJob} onClose={() => setApplyingJob(null)} />
      )}
    </section>
  );
}